import {z} from 'zod'
import {type HMPrepareDocumentChangeInput, type HMRequest, HMActionSchema, HMRequestSchema, packHmId} from './hm-types'
import {encode as cborEncode} from '@ipld/dag-cbor'
import {blake2b} from '@noble/hashes/blake2.js'
import {CID} from 'multiformats/cid'
import * as Digest from 'multiformats/hashes/digest'
import {deserialize} from 'superjson'
import {SeedClientError, SeedNetworkError, SeedValidationError} from './errors'
import type {AnySigner} from './signer'
import {createDocumentChange, createHomeGenesisChange, signDocumentChange} from './change'
import {createVersionRef} from './ref'

// ─── Query string serialization ─────────────────────────────────────────────

/**
 * Reserved query param key for non-object inputs (string, number, boolean).
 */
const PRIMITIVE_VALUE_KEY = '__value'
/**
 * Serializes data to a URL query string with proper type handling.
 * Non-object inputs (string, number, boolean) are stored under a __value key.
 */
function serializeQueryString<T>(data: T, _schema?: z.ZodType<T>): string {
  const params = new URLSearchParams()

  // Handle non-object inputs (string, number, boolean)
  if (typeof data !== 'object' || data === null) {
    if (data !== undefined && data !== null) {
      params.append(PRIMITIVE_VALUE_KEY, String(data))
    }
    const queryString = params.toString()
    return queryString ? `?${queryString}` : ''
  }

  for (const [key, value] of Object.entries(data as Record<string, unknown>)) {
    if (value === undefined || value === null) {
      continue
    }

    // Handle primitives directly
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
      params.append(key, String(value))
    } else {
      // Handle complex objects/arrays as JSON
      params.append(key, JSON.stringify(value))
    }
  }

  const queryString = params.toString()
  return queryString ? `?${queryString}` : ''
}

/**
 * Deserializes a URL query string using a Zod schema.
 * If the query contains only a __value key, returns the primitive value directly.
 */
export function deserializeQueryString<T>(queryString: string, schema: z.ZodType<T>): T {
  // Remove leading '?' if present
  const cleanQuery = queryString.startsWith('?') ? queryString.slice(1) : queryString

  if (!cleanQuery) {
    return schema.parse({})
  }

  const params = new URLSearchParams(cleanQuery)

  // Handle primitive value inputs
  if (params.has(PRIMITIVE_VALUE_KEY) && Array.from(params.keys()).length === 1) {
    return schema.parse(params.get(PRIMITIVE_VALUE_KEY))
  }

  const result: Record<string, unknown> = {}

  for (const [key, value] of Array.from(params.entries())) {
    // Try to parse as JSON first (for complex objects/arrays)
    try {
      result[key] = JSON.parse(value)
    } catch {
      // If JSON parsing fails, keep as string
      result[key] = value
    }
  }

  // Use Zod schema to parse and coerce to correct types
  return schema.parse(result)
}

/** Identifiers of the signed publication, available before the document index catches up. */
export type PublishDocumentResult = {version: string; genesis: string; generation: number}

export type PublishDocumentInput = {
  account: string
  changes: HMPrepareDocumentChangeInput['changes']
  path?: string
  baseVersion?: string
  genesis?: string
  generation?: number | bigint
  capability?: string
  visibility?: number
}

export type SeedClientOptions = {
  fetch?: typeof globalThis.fetch
  headers?: Record<string, string> | (() => Record<string, string> | Promise<Record<string, string>>)
}

/** Options that control an individual Seed API request. */
export type SeedClientRequestOptions = {
  signal?: AbortSignal
}

type PublishBlobsRequest = Extract<HMRequest, {key: 'PublishBlobs'}>

const DAG_CBOR_CODEC = 0x71
const BLAKE2B_256_CODE = 0xb220

const QUERY_PARAM_SERIALIZERS = {
  Account: (input: Extract<HMRequest, {key: 'Account'}>['input']) => ({
    id: input,
  }),
  Resource: (input: Extract<HMRequest, {key: 'Resource'}>['input']) => ({
    id: packHmId(input),
  }),
  ResourceMetadata: (input: Extract<HMRequest, {key: 'ResourceMetadata'}>['input']) => ({
    id: packHmId(input),
  }),
  ListCitations: (input: Extract<HMRequest, {key: 'ListCitations'}>['input']) => ({
    targetId: packHmId(input.targetId),
  }),
  ListChanges: (input: Extract<HMRequest, {key: 'ListChanges'}>['input']) => ({
    targetId: packHmId(input.targetId),
  }),
  ListCapabilities: (input: Extract<HMRequest, {key: 'ListCapabilities'}>['input']) => ({
    targetId: packHmId(input.targetId),
  }),
  ListRefs: (input: Extract<HMRequest, {key: 'ListRefs'}>['input']) => ({
    targetId: packHmId(input.targetId),
  }),
  DiscoveryStatus: (input: Extract<HMRequest, {key: 'DiscoveryStatus'}>['input']) => ({
    uid: input.uid,
    path: input.path.join('/'),
    ...(input.version ? {v: input.version} : {}),
    ...(input.latest ? {l: ''} : {}),
  }),
} as const satisfies Partial<{
  [K in HMRequest['key']]: (input: Extract<HMRequest, {key: K}>['input']) => Record<string, string>
}>

export type SeedClient = {
  request<K extends HMRequest['key']>(
    key: K,
    input: Extract<HMRequest, {key: K}>['input'],
    options?: SeedClientRequestOptions,
  ): Promise<Extract<HMRequest, {key: K}>['output']>
  /**
   * Store blobs. Goes through the site's `PublishBlobs` API; when the site refuses the request
   * body as too large (413 — a site may cap its API bodies at a few KB) and every blob names its
   * CID, the blobs are stored one per request through `putBlob` instead, and the result is the same.
   */
  publish(input: PublishBlobsRequest['input']): Promise<PublishBlobsRequest['output']>
  publishBlobs(input: PublishBlobsRequest['input']): Promise<PublishBlobsRequest['output']>
  /**
   * Store one blob through the daemon's raw blob endpoint, `POST /ipfs/<cid>`, which every Seed
   * site routes to its daemon with a file-sized body limit. The daemon recomputes the hash and
   * refuses bytes that do not match the CID; a stored blob is indexed like one from `PublishBlobs`.
   */
  putBlob(blob: {cid: string; data: Uint8Array}, options?: SeedClientRequestOptions): Promise<void>
  /** The CID the daemon assigns a DAG-CBOR blob published without one (blake2b-256). */
  daemonBlobCid(data: Uint8Array): string
  publishDocument(input: PublishDocumentInput, signer: AnySigner): Promise<PublishDocumentResult>
  baseUrl: string
}

export function createSeedClient(baseUrl: string, options?: SeedClientOptions): SeedClient {
  const normalizedBaseUrl = baseUrl.replace(/\/+$/, '')
  const fetchFn = options?.fetch ?? globalThis.fetch
  const getDefaultHeaders = async () =>
    typeof options?.headers === 'function' ? await options.headers() : options?.headers ?? {}

  async function request<Req extends HMRequest>(
    key: Req['key'],
    input: Req['input'],
    options?: SeedClientRequestOptions,
  ): Promise<Req['output']> {
    // Find matching schema from discriminated union
    const requestSchema = HMRequestSchema.options.find((schema) => schema.shape.key.value === key)
    if (!requestSchema) {
      throw new SeedValidationError(`Unknown request key: ${key}`)
    }

    let validatedInput: Req['input']
    try {
      validatedInput = requestSchema.shape.input.parse(input) as Req['input']
    } catch (err) {
      throw new SeedValidationError(`Invalid input for ${key}: ${err instanceof Error ? err.message : String(err)}`)
    }

    let response: Response
    const defaultHeaders = await getDefaultHeaders()

    const isAction = HMActionSchema.options.some((s) => s.shape.key.value === key)
    if (isAction) {
      const url = `${normalizedBaseUrl}/api/${key}`
      try {
        response = await fetchFn(url, {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/cbor',
            ...defaultHeaders,
          },
          body: new Uint8Array(cborEncode(stripUndefined(validatedInput))) as unknown as BodyInit,
          signal: options?.signal,
        })
      } catch (err) {
        if (isAbortError(err)) throw err
        throw new SeedNetworkError(
          `Network error fetching ${key}: ${err instanceof Error ? err.message : String(err)}`,
          {cause: err},
        )
      }
    } else {
      // Serialize input to query string (same logic as create-web-universal-client)
      const inputToParams = QUERY_PARAM_SERIALIZERS[key as keyof typeof QUERY_PARAM_SERIALIZERS]
      let queryString: string
      if (inputToParams) {
        const params = inputToParams(validatedInput as never)
        const searchParams = new URLSearchParams(params)
        queryString = searchParams.toString() ? `?${searchParams.toString()}` : ''
      } else if (!validatedInput) {
        queryString = ''
      } else {
        queryString = serializeQueryString(validatedInput, requestSchema.shape.input as any)
      }

      // Fetch
      const url = `${normalizedBaseUrl}/api/${key}${queryString}`
      try {
        response = await fetchFn(url, {
          method: 'GET',
          headers: {
            Accept: 'application/json',
            ...defaultHeaders,
          },
          signal: options?.signal,
        })
      } catch (err) {
        if (isAbortError(err)) throw err
        throw new SeedNetworkError(
          `Network error fetching ${key}: ${err instanceof Error ? err.message : String(err)}`,
          {cause: err},
        )
      }
    }

    if (!response.ok) {
      let errorBody: string | undefined
      try {
        errorBody = await response.text()
      } catch {
        // ignore
      }
      throw new SeedClientError(
        formatHTTPErrorMessage(key, response.status, response.statusText, errorBody),
        response.status,
        errorBody,
      )
    }

    // Deserialize the superjson-wrapped response. The document RPC passthroughs (QueryDocuments and
    // the attribute listings) answer with the daemon's plain protobuf JSON instead, unwrapped.
    const rawJson = await response.json()
    const deserialized =
      rawJson && typeof rawJson === 'object' && !Array.isArray(rawJson) && 'json' in rawJson
        ? deserialize(rawJson)
        : rawJson

    // Validate output with zod schema
    return requestSchema.shape.output.parse(deserialized) as Req['output']
  }

  /**
   * The CID the daemon gives a blob published without one: DAG-CBOR named by blake2b-256, the
   * hash Hypermedia uses for the blobs it creates (a `bafy2bzace…` version string is one).
   */
  function daemonBlobCid(data: Uint8Array): string {
    return CID.createV1(DAG_CBOR_CODEC, Digest.create(BLAKE2B_256_CODE, blake2b(data, {dkLen: 32}))).toString()
  }

  async function putBlob(blob: {cid: string; data: Uint8Array}, options?: SeedClientRequestOptions): Promise<void> {
    const url = `${normalizedBaseUrl}/ipfs/${blob.cid}`
    const defaultHeaders = await getDefaultHeaders()
    let response: Response
    try {
      response = await fetchFn(url, {
        method: 'POST',
        headers: {'Content-Type': 'application/octet-stream', ...defaultHeaders},
        body: new Uint8Array(blob.data) as unknown as BodyInit,
        signal: options?.signal,
      })
    } catch (err) {
      if (isAbortError(err)) throw err
      throw new SeedNetworkError(
        `Network error storing blob ${blob.cid}: ${err instanceof Error ? err.message : String(err)}`,
        {cause: err},
      )
    }
    if (!response.ok) {
      let errorBody: string | undefined
      try {
        errorBody = await response.text()
      } catch {
        // ignore
      }
      throw new SeedClientError(
        formatHTTPErrorMessage(`PutBlob ${blob.cid}`, response.status, response.statusText, errorBody),
        response.status,
        errorBody,
      )
    }
  }

  async function publish(input: PublishBlobsRequest['input']) {
    try {
      return await request<PublishBlobsRequest>('PublishBlobs', input)
    } catch (err) {
      if (!(err instanceof SeedClientError && err.status === 413)) throw err
      // The site's API body limit is not the daemon's: deliver the blobs through its blob endpoint,
      // naming an unaddressed blob the way the daemon would have.
      const cids: string[] = []
      for (const blob of input.blobs) {
        const cid = blob.cid || daemonBlobCid(blob.data)
        await putBlob({cid, data: blob.data})
        cids.push(cid)
      }
      return {cids}
    }
  }

  async function publishDocument(input: PublishDocumentInput, signer: AnySigner): Promise<PublishDocumentResult> {
    // Brand-new home documents need their deterministic genesis change created and
    // signed client-side — the server's PrepareChange cannot bootstrap it (it has no
    // signing key, and requires the genesis to already exist).
    if (!input.genesis && !input.baseVersion && !input.path) {
      const genesisChange = await createHomeGenesisChange(signer)
      const generation = input.generation != null ? Number(input.generation) : 1

      // Client-side change construction only supports setMetadata ops. When the home
      // document's first publish contains only metadata (e.g. account creation: name,
      // icon), we can build the whole document client-side in a single publish.
      if (input.changes.every((change) => change.op?.case === 'setMetadata')) {
        const contentChange = await createDocumentChange(
          {
            changes: input.changes,
            genesisCid: genesisChange.cid,
            deps: [genesisChange.cid],
            depth: 1,
          },
          signer,
        )
        const ref = await createVersionRef(
          {
            space: input.account,
            path: '',
            genesis: genesisChange.cid.toString(),
            version: contentChange.cid.toString(),
            generation,
            capability: input.capability,
          },
          signer,
        )
        await publish({
          blobs: [
            {data: genesisChange.bytes, cid: genesisChange.cid.toString()},
            {data: contentChange.bytes, cid: contentChange.cid.toString()},
            ...ref.blobs,
          ],
        })
        return {version: contentChange.cid.toString(), genesis: genesisChange.cid.toString(), generation}
      }

      // The first publish carries content blocks (moveBlock/replaceBlock/...), which
      // require the server's CRDT to build correctly. Publish the genesis change (plus a
      // Ref so it becomes a resolvable document — StoreBlobs indexes synchronously), then
      // fall through to the normal PrepareChange path with the genesis as the base.
      const genesisRef = await createVersionRef(
        {
          space: input.account,
          path: '',
          genesis: genesisChange.cid.toString(),
          version: genesisChange.cid.toString(),
          generation,
          capability: input.capability,
        },
        signer,
      )
      await publish({
        blobs: [{data: genesisChange.bytes, cid: genesisChange.cid.toString()}, ...genesisRef.blobs],
      })
      input = {
        ...input,
        baseVersion: genesisChange.cid.toString(),
        genesis: genesisChange.cid.toString(),
        generation,
      }
    }

    // Use PrepareDocumentChange for both new and existing documents so the server
    // can prepare all supported ops.
    const prepareInput = {
      account: input.account,
      path: input.path,
      baseVersion: input.baseVersion,
      changes: input.changes,
      capability: input.capability,
      visibility: input.visibility,
    }
    const {unsignedChange} = (await request('PrepareDocumentChange', prepareInput)) as Extract<
      HMRequest,
      {key: 'PrepareDocumentChange'}
    >['output']
    const {publishInput, changeCid, genesis, generation} = await signDocumentChange(
      {
        account: input.account,
        path: input.path,
        unsignedChange,
        genesis: input.genesis,
        generation: input.generation,
        capability: input.capability,
        visibility: input.visibility,
      },
      signer,
    )
    await publish(publishInput)
    return {version: changeCid.toString(), genesis, generation}
  }

  return {
    baseUrl: normalizedBaseUrl,
    request: request as SeedClient['request'],
    publish,
    publishBlobs: publish,
    putBlob,
    daemonBlobCid,
    publishDocument,
  }
}

function formatHTTPErrorMessage(key: string, status: number, statusText: string, body: string | undefined): string {
  const base = `HTTP ${status} from ${key}: ${statusText}`
  if (!body) return base

  try {
    const parsed = JSON.parse(body)
    if (parsed && typeof parsed === 'object' && typeof parsed.error === 'string' && parsed.error.trim()) {
      return `${base}: ${parsed.error}`
    }
  } catch {
    // If the response is not JSON, include a compact raw body below.
  }

  const trimmed = body.trim()
  return trimmed ? `${base}: ${trimmed}` : base
}

/** Remove undefined values from an object so CBOR encoding doesn't choke. */
function isAbortError(err: unknown): boolean {
  return typeof DOMException !== 'undefined' && err instanceof DOMException && err.name === 'AbortError'
}

function stripUndefined(obj: unknown): unknown {
  if (obj === null || obj === undefined || typeof obj !== 'object') return obj
  if (ArrayBuffer.isView(obj) || obj instanceof ArrayBuffer) return obj
  if (Array.isArray(obj)) return obj.map(stripUndefined)
  const result: Record<string, unknown> = {}
  for (const [k, v] of Object.entries(obj as Record<string, unknown>)) {
    if (v !== undefined) result[k] = stripUndefined(v)
  }
  return result
}
